// Pure helpers for the Sales Order form.

export const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', '6XL', '7XL', '8XL', '9XL', 'Free', 'Free Size'];

export function createEmptyRow(sr) {
  return {
    sr, product_id: '', design_code: '', designSearch: '', color: '',
    individualSizes: [], bands: [], sizeQty: {}, sizeRate: {}, addingColor: false,
    qty: '', rate: '', discount: '', amount: 0,
    colors: [], product: null, showDesignDropdown: false,
    openColorPicker: false, openSizePicker: false,
  };
}

// Colours available for a design: SKU colours (from PE) unioned with the
// colours tagged on catalogue images (image_colors values). The latter lets
// "forwarding" designs (no PE yet) still be ordered.
export function getDesignColors(product) {
  if (!product) return [];
  const skuColors = (product.skus || []).map(s => s.color);
  const imgColors = Object.values(product.image_colors || {}).filter(Boolean);
  return [...new Set([...skuColors, ...imgColors])];
}

// Available sizes (with resolved rate) for a product+color. When the design
// has no SKU for this colour yet (forwarding / custom colour), fall back to the
// standard size list at the design's tentative WSP so an order can still be
// placed. Rate is returned BLANK ('') when no price is recorded (not 0).
export function getDesignSizes(product, color, wspType, fallbackSizes = []) {
  if (!product || !color) return [];
  const skus = product.skus?.filter(s => s.color === color) || [];
  const order = (s) => { const i = SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };
  const blankIfZero = (v) => (v && v > 0 ? v : '');
  // Resolve a per-size tentative price from the design's size-pricing groups
  // (first matching group wins), falling back to the flat tentative_wsp.
  const groups = product.tentative_wsp_groups || [];
  const groupPrice = (sz) => {
    for (const g of groups) {
      if ((g.sizes || []).includes(sz) && g.price > 0) return g.price;
    }
    return null;
  };
  const flatTentative = product.tentative_wsp || 0;
  const tentativeFor = (sz) => { const gp = groupPrice(sz); return gp != null ? gp : flatTentative; };
  if (skus.length === 0) {
    return [...(fallbackSizes || [])].sort((a, b) => order(a) - order(b)).map(sz => ({ size: sz, rate: blankIfZero(tentativeFor(sz)) }));
  }
  const priceField = wspType === 'all' ? 'mrp' : 'wsp';
  const skuSizes = skus
    .map(s => {
      const base = s[priceField] || s.wsp;
      return { size: s.size, rate: blankIfZero(base && base > 0 ? base : tentativeFor(s.size)) };
    });
  // Always merge with fallback sizes so all active sizes are available
  // (user may order sizes not yet in stock — vendor will produce them)
  if (fallbackSizes.length > 0) {
    const skuSizeSet = new Set(skuSizes.map(s => s.size));
    for (const sz of fallbackSizes) {
      if (!skuSizeSet.has(sz)) {
        skuSizes.push({ size: sz, rate: blankIfZero(tentativeFor(sz)) });
      }
    }
  }
  return skuSizes.sort((a, b) => order(a.size) - order(b.size));
}

// Predefined size-range presets shown as quick "set" buttons. Each preset is
// only offered when at least 2 of its sizes exist for the design.
const SIZE_PRESETS = [
  { id: 'm-5xl', label: 'M–5XL', range: ['M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL'] },
  { id: 'm-xxl', label: 'M–XXL', range: ['M', 'L', 'XL', '2XL', 'XXL'] },
  { id: '3xl-5xl', label: '3XL–5XL', range: ['3XL', 'XXXL', '4XL', '5XL'] },
];

export function getSizeBands(individualSizes) {
  const order = (s) => { const i = SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };
  const available = new Set((individualSizes || []).map(s => s.size));
  const bands = [];
  for (const preset of SIZE_PRESETS) {
    const sizes = preset.range.filter(s => available.has(s)).sort((a, b) => order(a) - order(b));
    if (sizes.length >= 2) bands.push({ id: preset.id, label: preset.label, sizes });
  }
  return bands;
}

// Total amount for a row from its per-size qty/rate maps and the row discount.
export function recomputeRowAmount(row) {
  const disc = parseFloat(row.discount) || 0;
  let amt = 0;
  for (const [size, q] of Object.entries(row.sizeQty || {})) {
    const qn = parseInt(q) || 0;
    const r = parseFloat((row.sizeRate || {})[size]) || 0;
    amt += qn * r;
  }
  return Math.round(amt * (1 - disc / 100));
}

export function rowTotalQty(row) {
  return Object.values(row.sizeQty || {}).reduce((a, q) => a + (parseInt(q) || 0), 0);
}

export function sortScannedRows(rows) {
  const filled = rows.filter(r => r.product_id);
  const empty = rows.filter(r => !r.product_id);
  filled.sort((a, b) => {
    const dA = (a.design_code || '').toUpperCase(), dB = (b.design_code || '').toUpperCase();
    if (dA !== dB) return dA.localeCompare(dB, undefined, { numeric: true });
    return (a.color || '').toUpperCase().localeCompare((b.color || '').toUpperCase());
  });
  return [...filled.map((r, i) => ({ ...r, sr: i + 1 })), ...empty.map((r, i) => ({ ...r, sr: filled.length + i + 1 }))];
}

export function computeTotals(rows) {
  const grandTotal = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const grandQty = rows.reduce((s, r) => s + rowTotalQty(r), 0);
  return { grandTotal, grandQty };
}
